/**
 * OrgContext — shell for org-scoped state across the platform.
 *
 * Phase 0 lays the data layer only; Phase 1 wires the /consulting marketing
 * surface, the /org dashboard, and the BAM Partner Org badge keyed on
 * `currentMembership.org.is_partner`.
 *
 * What it exposes:
 *   orgs                    — array of orgs the current coach belongs to
 *                             (for an admin, every org)
 *   memberOrgs              : the orgs they are actually a member of: a real
 *                             org_members row, not an admin's staff view.
 *                             Never an intern location. The sidebar's
 *                             My Organization tile reads this.
 *   currentOrg              — the active org (persisted to localStorage so
 *                             the switcher picks back up where the coach
 *                             left off)
 *   currentMembership       — role + joined_at in `currentOrg`
 *   consultingSubscription  — active consulting_subscriptions row for
 *                             `currentOrg` (or null if none)
 *   partnerProgress         — { isPartner, monthsActive, monthsToGo }
 *                             derived from the sub's started_at
 *   loading                 — true until first fetch resolves
 *   refresh                 — manual reload (call after a mutation)
 *   setCurrentOrgId(id)     — switch active org
 *   isStaff                 — a BAM admin: sees every org, manages each
 *
 * BAM admins (Silva runs client orgs as one) get every org, not only the
 * ones they're a member of, with a synthetic "staff" membership where they
 * aren't (OrgDashboard treats staff as able to manage). Someone opening an
 * org from admin Organizations fires "bam:select-org" to switch to it.
 *
 * Why provider-level: most surfaces don't need org state, but the ones
 * that do (Consulting page, BAM Partner badge in profile cards, future
 * org-scoped admin panels) need a shared, cached source. Re-fetching per
 * component would mean N round-trips per page load.
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { supabase } from "../../supabaseClient";
import { deriveOrgPartnerProgress } from "../consulting/consultingApi";

const STORAGE_KEY = "bam_current_org_id";

const Ctx = createContext({
  orgs: [],
  memberOrgs: [],
  currentOrg: null,
  currentMembership: null,
  consultingSubscription: null,
  partnerProgress: { isPartner: false, monthsActive: 0, monthsToGo: 6 },
  loading: true,
  refresh: () => {},
  setCurrentOrgId: () => {},
  isStaff: false,
});

export function OrgProvider({ children }) {
  const [memberships, setMemberships] = useState([]);     // raw rows from org_members
  const [orgs, setOrgs] = useState([]);                    // joined organization rows
  const [consultingSubs, setConsultingSubs] = useState([]); // active subs across user's orgs
  const [currentOrgId, setCurrentOrgIdState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [isStaff, setIsStaff] = useState(false);
  const loadedOnce = React.useRef(false);
  const lastUserId = React.useRef(null);

  const refresh = useCallback(async () => {
    // Only the first load shows "Loading…". A later refresh (after a
    // mutation, or a sign-in) updates in place: setting loading again
    // unmounted the open tab, which lost open forms and in-flight uploads.
    if (!loadedOnce.current) setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      lastUserId.current = user?.id || null;
      if (!user) {
        setMemberships([]);
        setOrgs([]);
        setConsultingSubs([]);
        setLoading(false);
        return;
      }

      // 1. Memberships for this coach.
      const [{ data: mems, error: memErr }, { data: me }] = await Promise.all([
        supabase.from("org_members").select("id, org_id, role, joined_at").eq("user_id", user.id),
        supabase.from("profiles").select("is_admin").eq("id", user.id).maybeSingle(),
      ]);
      const staff = !!me?.is_admin;
      setIsStaff(staff);
      if (memErr && !staff) {
        // RLS will return 403/empty if the user has no memberships — that's
        // expected for the vast majority of coaches in Phase 0. Don't log
        // every hit.
        setMemberships([]);
        setOrgs([]);
        setConsultingSubs([]);
        setLoading(false);
        return;
      }
      let allMems = mems || [];
      if (staff) {
        // Every org, through the admin RPC; staff where not a member.
        const { data: all } = await supabase.rpc("admin_list_orgs");
        const have = new Set(allMems.map(m => m.org_id));
        allMems = [...allMems, ...(all || []).filter(o => !have.has(o.id))
          .map(o => ({ id: `staff-${o.id}`, org_id: o.id, role: "staff", joined_at: null }))];
      }
      setMemberships(allMems);

      const orgIds = allMems.map(m => m.org_id).filter(Boolean);
      if (orgIds.length === 0) {
        setOrgs([]);
        setConsultingSubs([]);
        setLoading(false);
        return;
      }

      // 2. Hydrate orgs.
      // kind (client / intern location) arrives with docs/intern-hub.sql.
      const COLS = "id, name, slug, logo_url, website, city, country, is_partner, partner_since, custom_price_cents, created_at";
      let { data: orgRows, error: orgErr } = await supabase.from("organizations").select(`${COLS}, kind`).in("id", orgIds);
      if (orgErr) ({ data: orgRows } = await supabase.from("organizations").select(COLS).in("id", orgIds));
      // Intern locations run in the Intern Hub, and teams (Advantage Flow only, like the Cavaliers) in
      // Advantage Flow: neither is the consulting portal.
      const locIds = new Set((orgRows || []).filter(o => o.kind === "location" || o.kind === "team").map(o => o.id));
      if (locIds.size) setMemberships(allMems.filter(m => !locIds.has(m.org_id)));
      setOrgs((orgRows || []).filter(o => !locIds.has(o.id)));

      // 3. Hydrate active consulting subscriptions for this user's orgs.
      const { data: subRows } = await supabase
        .from("consulting_subscriptions")
        .select("id, org_id, tier, status, current_period_end, started_at, canceled_at")
        .in("org_id", orgIds)
        .in("status", ["active", "trialing", "past_due"]);
      setConsultingSubs(subRows || []);
    } catch (e) {
      console.warn("[OrgContext] refresh failed:", e?.message);
    }
    loadedOnce.current = true;
    setLoading(false);
  }, []);

  useEffect(() => {
    let mounted = true;
    refresh();
    // Re-fetch when the signed-in person changes (sign in, sign out), not on
    // every auth event: the hourly TOKEN_REFRESHED used to reload it all.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      const uid = session?.user?.id || null;
      if (!mounted || uid === lastUserId.current) return;
      lastUserId.current = uid;
      loadedOnce.current = false;
      refresh();
    });
    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, [refresh]);

  // Default-pick: if no current org is stored, use the first one we have.
  // This keeps the switcher pre-populated for solo-org coaches without
  // requiring an explicit click.
  useEffect(() => {
    if (loading) return;
    if (orgs.length === 0) return;
    if (currentOrgId && orgs.some(o => o.id === currentOrgId)) return;
    setCurrentOrgIdState(orgs[0].id);
    try { localStorage.setItem(STORAGE_KEY, orgs[0].id); } catch {}
  }, [orgs, loading, currentOrgId]);

  const setCurrentOrgId = useCallback((id) => {
    setCurrentOrgIdState(id);
    try { localStorage.setItem(STORAGE_KEY, id || ""); } catch {}
  }, []);

  // Admin Organizations → "Open portal" picks the org to open.
  useEffect(() => {
    const on = (e) => { if (e?.detail) { setCurrentOrgId(e.detail); refresh(); } };
    window.addEventListener("bam:select-org", on);
    return () => window.removeEventListener("bam:select-org", on);
  }, [setCurrentOrgId, refresh]);

  const currentOrg = useMemo(
    () => orgs.find(o => o.id === currentOrgId) || null,
    [orgs, currentOrgId]
  );

  // Orgs with a real membership. The synthetic "staff" rows an admin gets
  // for every org don't count, and locations were filtered out above.
  const memberOrgs = useMemo(() => {
    const mine = new Set(memberships.filter(m => m.role !== "staff").map(m => m.org_id));
    return orgs.filter(o => mine.has(o.id));
  }, [orgs, memberships]);

  const currentMembership = useMemo(
    () => memberships.find(m => m.org_id === currentOrgId) || null,
    [memberships, currentOrgId]
  );

  const consultingSubscription = useMemo(
    () => consultingSubs.find(s => s.org_id === currentOrgId) || null,
    [consultingSubs, currentOrgId]
  );

  // Partner-tenure math, derived client-side from the sub's started_at.
  // Server keeps the authoritative `is_partner` column (auto-refreshed via
  // refresh_org_partner_status); we surface progress here so the UI can
  // show "Partner in 4 months" countdowns without an extra round-trip.
  const partnerProgress = useMemo(
    () => deriveOrgPartnerProgress({ org: currentOrg, consultingSubscription }),
    [currentOrg, consultingSubscription]
  );

  const value = useMemo(() => ({
    orgs,
    memberOrgs,
    currentOrg,
    currentMembership,
    consultingSubscription,
    partnerProgress,
    loading,
    refresh,
    setCurrentOrgId,
    isStaff,
  }), [orgs, memberOrgs, currentOrg, currentMembership, consultingSubscription, partnerProgress, loading, refresh, setCurrentOrgId, isStaff]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useOrg() {
  return useContext(Ctx);
}
